import { seasonRepository } from "../storage/storage";
import PersistenceError from "./exceptions/persistence-error";
import SportService from "./sport-service";
import Season from "../sports/common/season";
import GamedaySeasonSettingsProvider from "./providers/gameday-season-settings-provider";
import {
    applyConfigEdits,
    diffConfigEdits,
    mergeConfigSections,
    migrateClockConfig,
    normalizeFieldConfig,
    validateFieldConfig
} from "../sports/football/utils/field-config-utils";

class SeasonService {
    constructor() {
        this.settingsProvider = new GamedaySeasonSettingsProvider();
        this._refreshPromises = new Map();
    }

    setSeasonSettingsProvider(provider) {
        this.settingsProvider = provider;
    }

    async getBySeasonId(seasonId) {
        return seasonRepository.where("seasonId").equals(seasonId).first();
    }

    async saveSeason(season) {
        season.id = await seasonRepository.put(season);
        return season;
    }

    async createSeason(season) {
        try {
            const seasonId = await seasonRepository.put(season);
            return seasonRepository.getById(seasonId);
        } catch (err) {
            throw new PersistenceError(err);
        }
    }

    async getGameConfigurationOrDefault(
        seasonId,
        ruleSetId,
        skipRemoteRefresh = false
    ) {
        const season = await seasonRepository.getById(seasonId);

        if (!skipRemoteRefresh) {
            await this.refreshFromRemote(season.seasonId);
        }

        const updatedSeason = skipRemoteRefresh
            ? season
            : await seasonRepository.getById(seasonId);
        // The season's saved config only applies to its own ruleset: when a
        // game runs a different ruleset, fall back to that ruleset's
        // defaults. Same ruleset keeps the season config (event > season >
        // defaults precedence).
        const seasonConfig = migrateClockConfig(
            ruleSetId && ruleSetId !== updatedSeason.ruleSetId
                ? null
                : updatedSeason.gameConfiguration
        );

        const defaults = SportService.resolveDefaultConfig(
            updatedSeason.sport.sportId,
            ruleSetId || updatedSeason.ruleSetId
        );
        // Spread saved values over the defaults so fields added to the
        // schema after the config was saved still show up on the form.
        const gameConfiguration = mergeConfigSections(defaults, seasonConfig);

        const customFormData = SportService.resolveCustomFormData(
            updatedSeason.sport.sportId,
            ruleSetId || updatedSeason.ruleSetId
        );

        // The ruleset's own declared settings, reported alongside the resolved
        // config so a consumer can tell a setting this ruleset HAS from one a
        // previous ruleset left behind. The config deliberately keeps the
        // leftovers - a boxscore prices already-recorded plays from them - so
        // telling the two apart is the caller's job, not the merge's.
        return { gameConfiguration, customFormData, rulesetDefaults: defaults };
    }

    async saveGameConfiguration(seasonId, gameConfiguration) {
        const normalized = normalizeFieldConfig(gameConfiguration);
        validateFieldConfig(normalized.general);

        const season = await seasonRepository.getById(seasonId);
        season.gameConfiguration = normalized;
        const settingsUpdatedAt = new Date().toISOString();

        await seasonRepository.update(season.id, {
            gameConfiguration: normalized,
            settingsUpdatedAt
        });
        season.settingsUpdatedAt = settingsUpdatedAt;

        await this._pushRemoteSettings(season);

        return this.getGameConfigurationOrDefault(seasonId);
    }

    async saveRuleSetId(seasonId, ruleSetId) {
        const season = await seasonRepository.getById(seasonId);
        season.ruleSetId = ruleSetId;
        const settingsUpdatedAt = new Date().toISOString();

        await seasonRepository.update(season.id, {
            ruleSetId,
            settingsUpdatedAt
        });
        season.settingsUpdatedAt = settingsUpdatedAt;

        await this._pushRemoteSettings(season);

        return season;
    }

    /**
     * Change a season's rule set, carrying the crew's deliberate edits
     * (PSTATS-1380).
     *
     * The diff runs BEFORE the new id is written, because `season.ruleSetId`
     * is the only record of which rule set the saved config was authored
     * under. Writing the id first is what left the row incoherent - an id
     * naming one rule set beside a configuration holding another's - and it
     * also defeats the cross-rule-set guard in
     * `getGameConfigurationOrDefault`, which decides by comparing the two.
     *
     * Deliberately NOT `restoreDefaultGameConfiguration`. Wiping to defaults
     * is the other coherent answer and is the right one for the "Restore
     * defaults" button, but here it would destroy every season-level setting
     * the crew chose, locally and (via `_pushRemoteSettings`) remotely.
     */
    async applyRuleSetChange(seasonId, ruleSetId) {
        const season = await seasonRepository.getById(seasonId);

        // Already on this rule set, so there is nothing to re-express. The
        // rebuild below is not free: it drops any setting the rule set does
        // not declare - which reprices already-recorded plays - and bumps
        // settingsUpdatedAt, so _pushRemoteSettings would send a write that
        // last-write-wins could use to clobber a newer save elsewhere.
        if (season.ruleSetId === ruleSetId) {
            return season;
        }

        const sportId = season.sport.sportId;

        const edits = diffConfigEdits(
            SportService.resolveDefaultConfig(sportId, season.ruleSetId),
            migrateClockConfig(season.gameConfiguration)
        );

        const gameConfiguration = normalizeFieldConfig(
            applyConfigEdits(
                SportService.resolveDefaultConfig(sportId, ruleSetId),
                edits
            )
        );
        validateFieldConfig(gameConfiguration.general);

        const settingsUpdatedAt = new Date().toISOString();
        // Written together, so the row never describes two different rule sets.
        await seasonRepository.update(season.id, {
            ruleSetId,
            gameConfiguration,
            settingsUpdatedAt
        });
        season.ruleSetId = ruleSetId;
        season.gameConfiguration = gameConfiguration;
        season.settingsUpdatedAt = settingsUpdatedAt;

        await this._pushRemoteSettings(season);

        return season;
    }

    async restoreDefaultGameConfiguration(seasonId) {
        const season = await seasonRepository.getById(seasonId);
        return this.saveGameConfiguration(
            seasonId,
            SportService.resolveDefaultConfig(
                season.sport.sportId,
                season.ruleSetId
            )
        );
    }

    async saveOrUpdateSeasonByRemoteId(season) {
        const toUpdate = await seasonRepository
            .where({ seasonId: season.seasonId })
            .first();

        return this.saveSeason(Object.assign(toUpdate || new Season(), season));
    }

    async refreshFromRemote(externalSeasonId) {
        if (this._refreshPromises.has(externalSeasonId)) {
            return this._refreshPromises.get(externalSeasonId);
        }

        const promise = this._doRefreshFromRemote(externalSeasonId).finally(
            () => {
                this._refreshPromises.delete(externalSeasonId);
            }
        );

        this._refreshPromises.set(externalSeasonId, promise);
        return promise;
    }

    async _doRefreshFromRemote(externalSeasonId) {
        try {
            const remoteSettings = await this.settingsProvider.getSettings(
                externalSeasonId
            );

            if (remoteSettings) {
                const season = await seasonRepository
                    .where({ seasonId: externalSeasonId })
                    .first();

                if (season) {
                    const remoteIsNewer = this._areRemoteSettingsNewer(
                        season,
                        remoteSettings
                    );

                    if (remoteIsNewer) {
                        const changes = {
                            settingsUpdatedAt:
                                remoteSettings.updatedAt ||
                                new Date().toISOString()
                        };
                        if (remoteSettings.ruleSetId) {
                            changes.ruleSetId = remoteSettings.ruleSetId;
                        }
                        if (remoteSettings.gameConfiguration) {
                            changes.gameConfiguration =
                                remoteSettings.gameConfiguration;
                        }
                        await seasonRepository.update(season.id, changes);
                    }
                }
            }
        } catch (e) {
            // Best-effort: if remote fetch fails, continue with local data
            console.error(e);
        }
    }

    _areRemoteSettingsNewer(season, remoteSettings) {
        return (
            !season.settingsUpdatedAt ||
            !remoteSettings.updatedAt ||
            remoteSettings.updatedAt > season.settingsUpdatedAt
        );
    }

    async _pushRemoteSettings(season) {
        try {
            await this.settingsProvider.saveSettings(season.seasonId, {
                ruleSetId: season.ruleSetId,
                gameConfiguration: season.gameConfiguration,
                updatedAt: season.settingsUpdatedAt || new Date().toISOString()
            });
        } catch (e) {
            // Best-effort: if remote push fails, local data is already saved
        }
    }
}

export default new SeasonService();
